import { useState, useEffect, useMemo } from "react";
import { ToolsShell, primaryBtn, TOOLS_ACCENT } from "./ToolsShell.jsx";

// Currency Converter — fetches rates once per day from exchangerate.host
// (free, no API key, base-relative rates). Caches in localStorage with
// timestamp. Shows "Offline" badge prominently when cached rate is >24h
// old, per honesty rule.
//
// Why localStorage: the brief disallows persistent server state, but UI
// caching is explicitly fine ("Cache UI state ... is fine.") and the
// alternative — re-fetching on every page load even when offline — is
// worse for the user.

const CACHE_KEY = "nt_currency_cache_v1";
const STALE_AFTER_MS = 24 * 60 * 60 * 1000;
const API_BASE = "https://api.exchangerate.host/latest?base=USD";

const COMMON = [
  ["USD", "US Dollar"], ["EUR", "Euro"], ["GBP", "British Pound"], ["JPY", "Japanese Yen"],
  ["CNY", "Chinese Yuan"], ["INR", "Indian Rupee"], ["CAD", "Canadian Dollar"], ["AUD", "Australian Dollar"],
  ["CHF", "Swiss Franc"], ["MXN", "Mexican Peso"], ["BRL", "Brazilian Real"], ["KRW", "South Korean Won"],
  ["SEK", "Swedish Krona"], ["NOK", "Norwegian Krone"], ["NZD", "New Zealand Dollar"], ["SGD", "Singapore Dollar"],
  ["HKD", "Hong Kong Dollar"], ["ZAR", "South African Rand"], ["TRY", "Turkish Lira"], ["PLN", "Polish Zloty"],
  ["THB", "Thai Baht"], ["DKK", "Danish Krone"], ["IDR", "Indonesian Rupiah"], ["PHP", "Philippine Peso"],
];

const readCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.rates || !parsed.fetchedAt) return null;
    return parsed;
  } catch { return null; }
};
const writeCache = (rates) => {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify({ rates, fetchedAt: Date.now() })); } catch {}
};

export default function CurrencyConverter() {
  const [cache, setCache] = useState(() => readCache());
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [from, setFrom] = useState("USD");
  const [to, setTo] = useState("EUR");
  const [amount, setAmount] = useState("100");

  const fetchRates = async () => {
    setFetching(true); setFetchError(null);
    try {
      const r = await fetch(API_BASE);
      const j = await r.json();
      if (!j || !j.rates) throw new Error("Bad response");
      writeCache(j.rates);
      setCache({ rates: j.rates, fetchedAt: Date.now() });
    } catch (e) {
      setFetchError("Couldn't refresh rates. Using cached values.");
    } finally {
      setFetching(false);
    }
  };

  // Auto-fetch on mount if cache is stale or absent
  useEffect(() => {
    if (!cache || Date.now() - cache.fetchedAt > STALE_AFTER_MS) {
      fetchRates();
    }
    // eslint-disable-next-line
  }, []);

  const result = useMemo(() => {
    if (!cache || !cache.rates) return { err: "No rates available. Tap Refresh to fetch." };
    const amt = parseFloat(amount);
    if (isNaN(amt)) return { err: "Enter an amount." };
    const rFrom = from === "USD" ? 1 : cache.rates[from];
    const rTo = to === "USD" ? 1 : cache.rates[to];
    if (!rFrom || !rTo) return { err: "Currency not in rate table." };
    // Rates are USD-relative: convert from→USD→to
    const inUSD = amt / rFrom;
    const out = inUSD * rTo;
    return { value: out, rate: rTo / rFrom };
  }, [cache, amount, from, to]);

  const ageMs = cache ? Date.now() - cache.fetchedAt : Infinity;
  const stale = ageMs > STALE_AFTER_MS;
  const fmtAge = () => {
    if (!cache) return "never";
    const sec = ageMs / 1000;
    if (sec < 60) return `${Math.round(sec)}s ago`;
    const min = sec / 60;
    if (min < 60) return `${Math.round(min)} min ago`;
    const hr = min / 60;
    if (hr < 24) return `${Math.round(hr)} hr ago`;
    return `${Math.round(hr / 24)} days ago`;
  };

  const sel = {
    width: "100%", background: "#13141F", border: "1px solid #262839",
    borderRadius: 10, color: "#E8E9F3", fontSize: 15, padding: "12px 14px",
    boxSizing: "border-box",
  };
  const labelStyle = { fontSize: 13, color: "#9A9DBE", display: "block", marginBottom: 6, marginTop: 14 };

  return (
    <ToolsShell title="Currency Converter">
      {/* Honest staleness badge */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", background: stale ? "#1F1418" : "#13141F", border: `1px solid ${stale ? "#E0584E33" : "#262839"}`, borderRadius: 10, marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 11, color: "#7C8099", textTransform: "uppercase", letterSpacing: 0.4 }}>
            Rates fetched {fmtAge()}
            {stale && <span style={{ color: "#E0584E", marginLeft: 8 }}>● OFFLINE</span>}
          </div>
          <div style={{ fontSize: 11, color: "#5B5E7A", marginTop: 2 }}>
            {stale ? "Older than 24 hours — values may be inaccurate." : "Approx. mid-market rates, not the rate any specific exchange will give you."}
          </div>
        </div>
        <button onClick={fetchRates} disabled={fetching} style={{ background: "#1C1E2E", color: TOOLS_ACCENT, border: "none", borderRadius: 6, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: fetching ? "wait" : "pointer" }}>
          {fetching ? "…" : "Refresh"}
        </button>
      </div>

      {fetchError && (
        <div style={{ marginBottom: 10, padding: "8px 12px", background: "#1F1418", border: "1px solid #E0584E33", borderRadius: 8, fontSize: 12, color: "#D9A39E" }}>
          {fetchError}
        </div>
      )}

      <label style={labelStyle}>Amount</label>
      <input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" style={sel} />

      <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 8, alignItems: "end", marginTop: 14 }}>
        <div>
          <label style={labelStyle}>From</label>
          <select value={from} onChange={(e) => setFrom(e.target.value)} style={sel}>
            {COMMON.map(([code, name]) => <option key={code} value={code}>{code} — {name}</option>)}
          </select>
        </div>
        <button onClick={() => { const t = from; setFrom(to); setTo(t); }} style={{ background: "#1C1E2E", color: TOOLS_ACCENT, border: "none", borderRadius: 8, padding: "12px 12px", fontSize: 18, cursor: "pointer", marginBottom: 0 }}>⇄</button>
        <div>
          <label style={labelStyle}>To</label>
          <select value={to} onChange={(e) => setTo(e.target.value)} style={sel}>
            {COMMON.map(([code, name]) => <option key={code} value={code}>{code} — {name}</option>)}
          </select>
        </div>
      </div>

      <div style={{ marginTop: 22, background: "#13141F", border: `1px solid ${TOOLS_ACCENT}33`, borderRadius: 12, padding: "18px 20px" }}>
        {result.err ? (
          <div style={{ color: "#F8716A", fontSize: 14 }}>{result.err}</div>
        ) : (
          <>
            <div style={{ fontSize: 13, color: "#9A9DBE", marginBottom: 6 }}>{amount} {from} =</div>
            <div style={{ fontSize: 38, fontWeight: 800, color: TOOLS_ACCENT, fontVariantNumeric: "tabular-nums", lineHeight: 1.1 }}>
              {result.value.toFixed(2)} {to}
            </div>
            <div style={{ fontSize: 12, color: "#7C8099", marginTop: 8 }}>
              1 {from} = {result.rate.toFixed(4)} {to}
            </div>
          </>
        )}
      </div>

      <p style={{ fontSize: 12, color: "#5B5E7A", marginTop: 14 }}>
        Rates from exchangerate.host (mid-market, free, no API key). Banks and exchanges add spreads on top — actual transaction rates will differ.
      </p>
    </ToolsShell>
  );
}
